import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import CollectDataButton from '../../components/CollectDataButton';
import TeamStatsTiles from './components/TeamStatsTiles';
import TeamRoster from './components/TeamRoster';
import TeamActivityFeed from './components/TeamActivityFeed';
import InvitePanel from './components/InvitePanel';
import { useUserProfiles } from '../../context/UserProfilesContext';
import { classifySupabaseError } from '../../lib/supabase';
import { humanizeSupabaseError } from '../../lib/supabaseErrors';

// ---------------------------------------------------------------------------
// Multi-User Collaboration Hub — the real one.
//
// This page used to be a mock: fake chat rooms, fake presence indicators,
// fake shared documents. It's now the workspace's collaboration overview:
//
//   * TeamStatsTiles — "how much did we do together this week" numbers
//   * TeamRoster     — everyone with a login, with role + discipline
//   * TeamActivityFeed — who logged what, chronological, attributed
//   * InvitePanel    — how to add another teammate
//
// All the data is REAL: it pulls from the same Supabase tables the rest
// of the app uses, which means when the OT logs a fine-motor data point
// on Marcus, the classroom teacher sees it here within seconds.
// ---------------------------------------------------------------------------

const MultiUserCollaborationHub = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { error, loading, isLive } = useUserProfiles();

  // Belt-and-suspenders classification. UserProfilesContext already
  // classifies the fetch error to { kind: 'schema_setup', table } for
  // us — but a stale bundle, a caching CDN, or a rewrap along the way
  // can leave `error` as a bare string that reads like the raw
  // Postgres text. Re-classify here so we NEVER end up rendering the
  // scary "Could not find the table 'public.user_profiles' in the
  // schema cache" line straight at a teacher.
  const schemaError =
    error && typeof error === 'object' && error.kind === 'schema_setup'
      ? error
      : (() => {
          const c = classifySupabaseError(error);
          if (c?.kind === 'missing_table') {
            return {
              kind: 'schema_setup',
              table: c.table || 'user_profiles',
              message: typeof error === 'string' ? error : '',
            };
          }
          return null;
        })();

  // If we still have a non-schema error, humanize it so a raw Postgres
  // string never lands on the page.
  const genericErrorMessage =
    !schemaError && error && (typeof error === 'string' || error.message)
      ? humanizeSupabaseError(error, "Couldn't load your team.")
      : null;

  return (
    <>
      <Helmet>
        <title>Team Hub - EduTracker Pro</title>
        <meta
          name="description"
          content="See your team, the data everyone's logging, and how to invite the OT, PT, SLP, behavior specialist, or paras who work with your students."
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header
          onMenuToggle={() => setIsSidebarOpen((v) => !v)}
          isMenuOpen={isSidebarOpen}
        />
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed((v) => !v)}
        />

        <main
          className={`transition-all duration-300 ${
            isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
          } pt-16`}
        >
          <div className="p-6 max-w-7xl mx-auto">
            <Breadcrumb />

            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-1">
                  Your team
                </h1>
                <p className="text-muted-foreground max-w-2xl">
                  Everyone who works with your students in one place —
                  the classroom teacher, para, SLP, OT, PT, behavior
                  specialist, and case manager. When any of them logs
                  an incident or a data point, it shows up here so
                  the whole team can see it.
                </p>
              </div>
              <div className="flex flex-shrink-0">
                <CollectDataButton
                  size="lg"
                  variant="default"
                  label="Collect data"
                />
              </div>
            </div>

            {/* Schema-not-yet-set-up state: friendly amber "here's one
                thing your tech coordinator needs to do" panel. Absolutely
                does NOT include the raw "Could not find the table
                'public.user_profiles' in the schema cache" text — that's
                the whole point of this branch. */}
            {schemaError && (
              <div
                role="alert"
                className="mb-6 rounded-lg border-2 border-amber-300 bg-amber-50 p-5"
              >
                <div className="flex items-start gap-3">
                  <Icon
                    name="Info"
                    size={20}
                    className="text-amber-700 mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1">
                    <p className="text-base font-semibold text-amber-900">
                      Your team page isn't set up yet
                    </p>
                    <p className="text-sm text-amber-900 mt-2">
                      This is a one-time step your school's tech
                      coordinator (or district IT support) has to do —
                      not something you can fix from here. Everything
                      else in the app keeps working while this is
                      pending.
                    </p>
                    <p className="text-sm text-amber-900 mt-2">
                      When you see them, ask them to run the file{' '}
                      <a
                        href="https://github.com/Senergy-hub/edutracker_pro/blob/main/supabase/APPLY_ALL_MIGRATIONS.sql"
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-2 font-medium"
                      >
                        supabase/APPLY_ALL_MIGRATIONS.sql
                      </a>{' '}
                      in the Supabase SQL Editor. It only takes about a
                      minute.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Non-schema error state: humanized message only. Never
                surface a raw Postgres string. */}
            {genericErrorMessage && (
              <div
                role="alert"
                className="mb-6 rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3"
              >
                <Icon
                  name="AlertCircle"
                  size={18}
                  className="text-destructive mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-destructive">
                    Couldn't load your team
                  </p>
                  <p className="text-xs text-destructive/80 mt-0.5">
                    {genericErrorMessage}
                  </p>
                </div>
              </div>
            )}

            {!isLive && !loading && (
              <div
                role="status"
                className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
              >
                <Icon
                  name="AlertTriangle"
                  size={18}
                  className="text-amber-700 mt-0.5"
                />
                <div className="text-amber-900">
                  <p className="text-sm font-medium">Demo mode</p>
                  <p className="text-xs mt-0.5">
                    You're seeing a sample team so the page renders. Sign in
                    with Supabase credentials to see your real teammates and
                    their live activity.
                  </p>
                </div>
              </div>
            )}

            {/* Only render the roster / stats / activity feed if we
                can actually load teammates. Without the user_profiles
                table those components would just show empty state
                below an already-explanatory amber panel, which is
                more confusing than useful. */}
            {!schemaError && (
              <>
                <div className="mb-6">
                  <TeamStatsTiles />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  <div className="xl:col-span-1 space-y-6">
                    <TeamRoster />
                    <InvitePanel />
                  </div>
                  <div className="xl:col-span-2">
                    <TeamActivityFeed limit={15} />
                  </div>
                </div>
              </>
            )}

            <footer className="mt-10 pt-6 border-t border-border text-xs text-muted-foreground">
              <p>
                All teammates share the same students, goals, incidents, and
                reports. Access is controlled by your Supabase project's
                authentication — remove a user there to revoke access
                immediately.
              </p>
            </footer>
          </div>
        </main>
      </div>
    </>
  );
};

export default MultiUserCollaborationHub;
